/**
 * Utilitaires pour convertir les mappings Elasticsearch en FieldsMapping
 * compatible avec le WidgetDataConfigurator
 *
 * Supports type-prefixed fields (num$, text$, bool$, date$, obj$) for ES indexing.
 */

import { AvailableFilter } from "@/models/filter";
import { FieldsMapping, FieldType } from "@/models/widget-data-config";
import { getDefaultNormalizedFields } from "./normalized-model-fields";

// ES type prefixes used for field naming
export const ES_PREFIX_SEP = "$";
export const ES_PREFIXES = [
  "num$",
  "text$",
  "bool$",
  "date$",
  "obj$",
] as const;
export type EsTypePrefix = "num" | "text" | "bool" | "date" | "obj";

/**
 * Parse a prefixed field name to extract the original name and type.
 *
 * @param field - The prefixed field name (e.g., "num$likes", "text$username")
 * @returns Object with name (unprefixed) and type (prefix without $)
 *
 * @example
 * parseField("num$likes") // { name: "likes", type: "num" }
 * parseField("text$username") // { name: "username", type: "text" }
 * parseField("unknown") // { name: "unknown", type: "text" } (fallback)
 */
export const parseField = (
  field: string
): { name: string; type: EsTypePrefix } => {
  for (const prefix of ES_PREFIXES) {
    if (field.startsWith(prefix)) {
      return {
        name: field.slice(prefix.length),
        type: prefix.slice(0, -1) as EsTypePrefix,
      };
    }
  }
  return { name: field, type: "text" }; // Fallback for unprefixed fields
};

/**
 * Check if a field name is prefixed with an ES type prefix.
 *
 * @param field - The field name to check
 * @returns True if the field has a valid ES prefix
 */
export const isPrefixedField = (field: string): boolean => {
  return ES_PREFIXES.some((prefix) => field.startsWith(prefix));
};

/**
 * Get the display name for a field (without prefix).
 *
 * @param field - The prefixed field name
 * @returns The field name without the type prefix
 *
 * @example
 * getDisplayName("num$likes") // "likes"
 * getDisplayName("obj$actor.text$name") // "actor.name"
 */
export const getDisplayName = (field: string): string => {
  // Handle nested prefixed fields (e.g., "obj$actor.text$name")
  return field
    .split(".")
    .map((part) => parseField(part).name)
    .join(".");
};

/**
 * Convert an ES type prefix to a FieldType for widget configuration.
 *
 * @param prefixType - The ES type prefix (without $)
 * @returns The corresponding FieldType
 */
export const prefixToFieldType = (prefixType: EsTypePrefix): FieldType => {
  const mapping: Record<EsTypePrefix, FieldType> = {
    num: "number",
    text: "text",
    bool: "text", // Booleans displayed as text in UI
    date: "date",
    obj: "text", // Objects displayed as text in UI
  };
  return mapping[prefixType];
};

/**
 * Convert a list of prefixed field names to a FieldsMapping.
 * This is used with the new /fields endpoint that returns prefixed field names.
 *
 * @param fields - Array of prefixed field names from the /fields endpoint
 * @returns FieldsMapping compatible with widget configuration
 *
 * @example
 * convertPrefixedFieldsToMapping(["num$likes", "text$username", "date$created_at"])
 * // { "num$likes": "number", "text$username": "text", "date$created_at": "date" }
 */
export const convertPrefixedFieldsToMapping = (
  fields: string[]
): FieldsMapping => {
  const mapping: FieldsMapping = {};

  // Add default normalized fields first
  const defaultNormalizedFields = getDefaultNormalizedFields();
  defaultNormalizedFields.forEach((field) => {
    const fieldType = mapElasticsearchTypeToFieldType(field.type);
    mapping[field.key] = fieldType;
  });

  // Add prefixed fields from the /fields endpoint
  fields.forEach((field) => {
    const { type } = parseField(field);
    mapping[field] = prefixToFieldType(type);

    // For text fields, also add the .keyword variant
    if (type === "text") {
      mapping[`${field}.keyword`] = "text";
    }
  });

  return mapping;
};

/**
 * Convert a list of prefixed fields to AvailableFilter array for UI display.
 *
 * @param fields - Array of prefixed field names
 * @returns Array of AvailableFilter with display-friendly names
 */
export const convertPrefixedFieldsToFilters = (
  fields: string[]
): AvailableFilter[] => {
  return fields.map((field) => {
    const { name, type } = parseField(field);
    return {
      key: field, // Keep the prefixed name for queries
      type: prefixToFieldType(type),
      enabled: true,
      // The display name can be accessed via getDisplayName(field)
    };
  });
};

/**
 * Convertit un type Elasticsearch en FieldType
 * Note: Les champs "keyword" sont retournés comme "text" pour l'affichage utilisateur
 * La résolution vers .keyword pour ES est gérée par resolveField dans widget-data-configurator
 */
export const mapElasticsearchTypeToFieldType = (
  elasticsearchType: string
): FieldType => {
  const normalizedType = elasticsearchType.toLowerCase();

  // Types de texte - tous affichés comme "text" pour l'utilisateur
  // (keyword est un détail d'implémentation ES pour les agrégations)
  if (
    normalizedType === "keyword" ||
    normalizedType === "text" ||
    normalizedType === "string"
  ) {
    return "text";
  }

  // Types numériques
  if (
    normalizedType === "long" ||
    normalizedType === "integer" ||
    normalizedType === "short" ||
    normalizedType === "byte" ||
    normalizedType === "double" ||
    normalizedType === "float" ||
    normalizedType === "half_float" ||
    normalizedType === "scaled_float" ||
    normalizedType === "number"
  ) {
    return "number";
  }

  // Types de date
  if (
    normalizedType === "date" ||
    normalizedType === "date_nanos" ||
    normalizedType === "datetime"
  ) {
    return "date";
  }

  // Par défaut, considérer comme texte
  return "text";
};

/**
 * Convertit un tableau d'AvailableFilter en FieldsMapping
 * Privilégie les champs ".keyword" pour les champs text si disponibles
 *
 * IMPORTANT: Les champs normalisés (NormalizedModel) sont toujours ajoutés par défaut,
 * même s'ils ne sont pas présents dans les availableFilters
 */
export const convertAvailableFiltersToFieldsMapping = (
  availableFilters: AvailableFilter[]
): FieldsMapping => {
  const mapping: FieldsMapping = {};
  const textFieldsWithKeyword = new Set<string>(); // Pour tracker les champs text qui ont un .keyword

  // ÉTAPE 0 : Ajouter les champs normalisés par défaut
  // Ces champs sont toujours disponibles dans le schéma NormalizedModel
  const defaultNormalizedFields = getDefaultNormalizedFields();
  defaultNormalizedFields.forEach((field) => {
    // Convertir le type Elasticsearch en FieldType
    const fieldType = mapElasticsearchTypeToFieldType(field.type);
    mapping[field.key] = fieldType;
  });

  // Première passe : identifier les champs .keyword et leurs champs text parents
  availableFilters.forEach((filter) => {
    if (filter.key && filter.type) {
      // Si c'est un champ .keyword, noter le champ parent (sans .keyword)
      if (filter.key.endsWith(".keyword")) {
        const parentField = filter.key.replace(/\.keyword$/, "");
        textFieldsWithKeyword.add(parentField);
        // Ajouter le champ .keyword avec le type "text" (affiché comme text pour l'utilisateur)
        mapping[filter.key] = "text";
      }
    }
  });

  // Deuxième passe : ajouter tous les champs
  availableFilters.forEach((filter) => {
    if (filter.key && filter.type) {
      const fieldType = mapElasticsearchTypeToFieldType(filter.type);

      // Pour les champs text :
      // - Si un .keyword existe, on préfère utiliser le .keyword (déjà ajouté en première passe)
      // - Sinon, on ajoute le champ text normalement
      if (fieldType === "text") {
        // Si ce champ text a un .keyword, on ne l'ajoute pas (on utilise le .keyword)
        if (textFieldsWithKeyword.has(filter.key)) {
          return;
        }
        // Sinon, ajouter le champ text normalement
        mapping[filter.key] = "text";
        return;
      }

      // Pour les autres types, ajouter normalement
      // Les champs du mapping Elasticsearch peuvent écraser les champs normalisés par défaut
      if (!filter.key.endsWith(".keyword") || !mapping[filter.key]) {
        mapping[filter.key] = fieldType;
      }
    }
  });

  return mapping;
};

/**
 * Convertit un mapping Elasticsearch brut (objet) en FieldsMapping
 * Exclut les champs de type "text" et privilégie les champs ".keyword"
 *
 * IMPORTANT: Les champs normalisés (NormalizedModel) sont toujours ajoutés par défaut,
 * même s'ils ne sont pas présents dans le mapping Elasticsearch
 */
export const convertElasticsearchMappingToFieldsMapping = (
  elasticsearchMapping: any
): FieldsMapping => {
  const mapping: FieldsMapping = {};

  // ÉTAPE 0 : Ajouter les champs normalisés par défaut
  // Ces champs sont toujours disponibles dans le schéma NormalizedModel
  const defaultNormalizedFields = getDefaultNormalizedFields();
  defaultNormalizedFields.forEach((field) => {
    // Convertir le type Elasticsearch en FieldType
    const fieldType = mapElasticsearchTypeToFieldType(field.type);
    mapping[field.key] = fieldType;
  });

  // Fonction récursive pour parcourir les propriétés
  const traverseProperties = (obj: any, prefix: string = "") => {
    if (!obj || typeof obj !== "object") {
      return;
    }

    // Si l'objet a directement un type (champ simple)
    if (obj.type && !obj.properties) {
      if (prefix) {
        const fieldType = mapElasticsearchTypeToFieldType(obj.type);
        // Ne pas ajouter les champs text purs
        if (fieldType !== "text") {
          mapping[prefix] = fieldType;
        }
      }
      return;
    }

    // Si l'objet a des propriétés (champ complexe/nested)
    if (obj.properties && typeof obj.properties === "object") {
      Object.keys(obj.properties).forEach((key) => {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        const property = obj.properties[key];

        // Vérifier d'abord si le champ a des sous-champs "fields" (comme .keyword)
        const hasKeywordSubfield = property.fields && property.fields.keyword;

        // Si la propriété a un type direct (sans properties)
        if (property.type && !property.properties) {
          const fieldType = mapElasticsearchTypeToFieldType(property.type);

          // Si c'est un champ text avec un sous-champ keyword, ne pas ajouter le champ text
          // Le sous-champ keyword sera ajouté plus bas
          if (fieldType === "text" && hasKeywordSubfield) {
            // Ne pas ajouter le champ text parent, on ajoutera le .keyword plus bas
          } else if (fieldType !== "text") {
            // Ajouter les autres types (keyword, number, date, etc.)
            mapping[fullKey] = fieldType;
          }
          // Si c'est un text sans keyword, on ne l'ajoute pas (exclu)
        } else if (property.properties) {
          // Propriété imbriquée, continuer la récursion
          traverseProperties(property, fullKey);
        } else if (property.type) {
          // Cas où la propriété a un type mais aussi d'autres propriétés
          const fieldType = mapElasticsearchTypeToFieldType(property.type);
          if (fieldType === "text" && hasKeywordSubfield) {
            // Ne pas ajouter le champ text parent
          } else if (fieldType !== "text") {
            mapping[fullKey] = fieldType;
          }
        }

        // Gérer les sous-champs "fields" (comme .keyword pour les champs text)
        if (property.fields && typeof property.fields === "object") {
          Object.keys(property.fields).forEach((subFieldKey) => {
            const subField = property.fields[subFieldKey];
            const subFieldFullKey = `${fullKey}.${subFieldKey}`;

            // Si le sous-champ a un type, l'ajouter
            if (subField.type) {
              const subFieldType = mapElasticsearchTypeToFieldType(
                subField.type
              );
              // Ajouter les sous-champs (comme .keyword)
              mapping[subFieldFullKey] = subFieldType;
            }
          });
        }
      });
    }

    // Gérer les sous-champs "fields" au niveau de l'objet courant (si pas dans properties)
    if (obj.fields && typeof obj.fields === "object" && !obj.properties) {
      Object.keys(obj.fields).forEach((subFieldKey) => {
        const subField = obj.fields[subFieldKey];
        const fullKey = prefix ? `${prefix}.${subFieldKey}` : subFieldKey;

        // Si le sous-champ a un type, l'ajouter
        if (subField.type) {
          const fieldType = mapElasticsearchTypeToFieldType(subField.type);
          // Ajouter les sous-champs (comme .keyword)
          mapping[fullKey] = fieldType;
        }
      });
    }
  };

  // Gérer différentes structures de mapping Elasticsearch
  // Structure 1: { mappings: { properties: { ... } } }
  if (
    elasticsearchMapping.mappings &&
    elasticsearchMapping.mappings.properties
  ) {
    traverseProperties(elasticsearchMapping.mappings.properties, "");
    return mapping;
  }

  // Structure 2: { properties: { ... } }
  if (elasticsearchMapping.properties && !elasticsearchMapping.mappings) {
    traverseProperties(elasticsearchMapping.properties, "");
    return mapping;
  }

  // Structure 3: { field1: { properties: { ... } }, field2: { type: "..." } }
  Object.keys(elasticsearchMapping).forEach((key) => {
    const fieldMapping = elasticsearchMapping[key];

    // Si le champ racine a directement un type
    if (fieldMapping.type && !fieldMapping.properties) {
      mapping[key] = mapElasticsearchTypeToFieldType(fieldMapping.type);
    } else {
      // Sinon, parcourir récursivement
      traverseProperties(fieldMapping, key);
    }
  });

  return mapping;
};

/**
 * Normalise un nom de champ en retirant le suffixe .keyword
 * @param field - Le nom du champ à normaliser
 * @returns Le nom du champ sans .keyword
 */
export const normalizeFieldName = (
  field: string | null | undefined
): string => {
  if (!field) return "";
  return field.replace(/\.keyword$/, "");
};

/**
 * Résout le vrai nom du champ dans le mapping
 * Ajoute .keyword si nécessaire pour les champs texte
 * @param field - Le nom du champ normalisé
 * @param fieldsMapping - Le mapping des champs
 * @returns Le nom du champ tel qu'il existe dans Elasticsearch
 */
export const resolveFieldName = (
  field: string | null,
  fieldsMapping: FieldsMapping
): string | null => {
  if (!field) return null;

  // Si le champ existe tel quel dans le mapping, c'est bon
  if (fieldsMapping[field]) return field;

  // Si le champ + .keyword existe, on l'utilise
  const keywordField = `${field}.keyword`;
  if (fieldsMapping[keywordField]) return keywordField;

  // Sinon on retourne le champ tel quel (cas fallback)
  return field;
};

/**
 * Convertit un FieldsMapping en tableau d'AvailableFilter[]
 * Les clés sont conservées au format type$name pour les requêtes backend
 * Les champs .keyword sont dédupliqués
 * @param fieldsMapping - Le mapping des champs
 * @param filterByTypes - Types optionnels pour filtrer les champs
 * @returns Un tableau d'AvailableFilter triés par clé
 */
export const fieldsMappingToAvailableFilters = (
  fieldsMapping: FieldsMapping,
  filterByTypes?: string[]
): AvailableFilter[] => {
  const filters = new Map<string, AvailableFilter>();

  Object.entries(fieldsMapping).forEach(([key, type]) => {
    // Retirer .keyword pour la déduplication mais conserver le format type$name
    const normalizedKey = normalizeFieldName(key);
    const typeStr = type as string;

    // Filtrer par types si demandé
    if (
      filterByTypes &&
      filterByTypes.length > 0 &&
      !filterByTypes.includes(typeStr)
    ) {
      return;
    }

    // On garde le type du champ - tous les types texte sont maintenant "text"
    // Utiliser normalizedKey pour la déduplication, mais conserver la clé originale
    if (!filters.has(normalizedKey)) {
      filters.set(normalizedKey, {
        key: normalizedKey, // Conserver le format type$name (sans .keyword)
        type: typeStr,
        enabled: true,
      });
    }
  });

  return Array.from(filters.values()).sort((a, b) =>
    a.key.localeCompare(b.key)
  );
};
